import React, { useEffect, useRef, useState } from "react";
import EbDialog from "./EbDialog";
import WigFieldset, {
  canSubmitWig,
  toWigPayload,
  wigPhraseFor,
} from "./WigFieldset";
import { LIVE_REVIEW_SECTION_MAX_ITEMS } from "../_socket/LiveReviewSocketStore";
import "./AddWigRow.scss";

// ═════════════════════════════════════════════════════════════════════════════
// "Add a WIG" as a MODAL — the connections map's version of `AddWigRow`.
//
// ⚠️ IDENTICAL IN TWO REPOSITORIES.
//
// ── Why this one is a dialog when the other two are inline ──────────────────
// On the finalize screen and the Year 1 plan, the composer opens in the list:
// the new WIG has to make sense against the others in its dimension, and the
// list is right there to read.
//
// The map is a different shape of screen and the same reasoning gives the
// opposite answer. Its cards are a fixed 200px grid cell — three text fields in
// that column would be a stack of slivers — and the canvas pans and zooms under
// a transform, so a form growing inside a band would reflow the very geometry
// the connector lines are measured against, mid-typing. A modal sits outside
// all of that: the map holds still, and the card appears when it is done.
//
// The FIELDS are not this file's to decide — they come from `WigFieldset`, so
// the wording, the limits and the required marks cannot drift from the inline
// version. Only the frame differs, which is the whole point of the split.
// ═════════════════════════════════════════════════════════════════════════════

const EMPTY_DRAFT = { label: "", value: "", rationale: "" };

const PlusIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * @param sectionKey   The dimension being added to; handed back to `onAdd`.
 * @param sectionLabel Its name, for the title ("Financial").
 * @param count        How many WIGs it holds now, for the "4 of 10" line.
 * @param pending      True while the add is in flight.
 * @param onAdd        `(sectionKey, { label, value, rationale }) => void`
 * @param onClose      Cancel, Escape, backdrop.
 */
const AddWigDialog = ({
  sectionKey,
  sectionLabel = "",
  count = 0,
  pending = false,
  onAdd,
  onClose,
}) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const firstFieldRef = useRef(null);

  // ⚠️ FOCUS IS TAKEN DELIBERATELY, AND AFTER A FRAME. `EbDialog` focuses its
  // own autofocus action on mount; without this the caret would land on the Add
  // button of an empty form. The timeout puts this last, so the field wins.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const canSubmit = canSubmitWig(draft) && !pending;

  const submit = () => {
    if (!canSubmit || !onAdd) return;
    onAdd(sectionKey, toWigPayload(draft));
  };

  // Cmd/Ctrl+Enter submits; Escape is `EbDialog`'s own and closes the dialog.
  // Bare Enter is left alone for the same reason as the inline composer: two of
  // the three fields are textareas holding sentences someone types line breaks
  // into while thinking.
  const onKeyDown = (event) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <EbDialog
      isOpen
      size="md"
      tone="primary"
      icon={<PlusIcon />}
      title={`Add ${wigPhraseFor(sectionLabel)}`}
      subtitle={`This will be WIG ${count + 1} of up to ${LIVE_REVIEW_SECTION_MAX_ITEMS} in this dimension. It starts with no connections — draw them on the map once it's in.`}
      busy={pending}
      onClose={onClose}
      actions={[
        { label: "Cancel", variant: "ghost", onClick: onClose },
        {
          label: "Add WIG",
          loadingLabel: "Adding…",
          variant: "primary",
          onClick: submit,
          loading: pending,
          disabled: !canSubmit,
        },
      ]}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="awr-dialog-fields" onKeyDown={onKeyDown}>
        <WigFieldset
          idPrefix={`awd-${sectionKey}`}
          draft={draft}
          onChange={(next) => setDraft((prev) => ({ ...prev, ...next }))}
          firstFieldRef={firstFieldRef}
        />
      </div>
    </EbDialog>
  );
};

export default AddWigDialog;
